import { useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Icon from 'Components/Atoms/Icon/Icon';
import InvoicesInboxItem from './InvoicesInboxItem';
import useInfiniteInvoicesEmails from 'Hooks/useInfiniteInvoicesEmails';
import Loading from 'Components/Atoms/Loading/Loading';
import { useTranslation } from 'react-i18next';
import Spinner from 'Components/Atoms/Spinner';
import OutlineArrowBottom from 'Components/Atoms/OutlineIcons/OutlineArrowBottom';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import { useQueryClient } from '@tanstack/react-query';
import Search from 'Components/Atoms/Search/Search';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function InvoicesInboxList(props) {
    const { onViewEmail } = props;

    const [inputQuery, setInputQuery] = useState('');
    const [query, setQuery] = useState(null);
    const [isForceRefetch, setIsForceRefetch] = useState(false);

    const queryClient = useQueryClient();
    const { t } = useTranslation('invoices');
    const { data, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteInvoicesEmails({
        query,
    });

    const sentinelRef = useRef(null);

    useEffect(() => {
        if (isFetching) return;

        if (sentinelRef.current) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        fetchNextPage();
                    }
                });
            });

            observer.observe(sentinelRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [sentinelRef, isFetching]);

    const emails = useMemo(() => data?.pages?.flatMap((page) => page.items) ?? [], [data]);

    const handleRefetch = async () => {
        setIsForceRefetch(true);

        await Promise.all([wait(1500), refetch()]);
        await queryClient.invalidateQueries({ queryKey: ['invoices'] });
        setIsForceRefetch(false);
    };

    return (
        <>
            <div className="mb-3 pt-2 flex gap-2 items-center">
                <Search
                    className="grow"
                    label={t('inbox-search-label')}
                    value={inputQuery}
                    onChange={setInputQuery}
                    placeholder={t('inbox-search-placeholder')}
                    onSearch={(querySearch) => {
                        const search = querySearch.trim() === '' ? null : querySearch.trim();
                        setQuery(search);
                    }}
                    onClear={() => {
                        setQuery(null);
                        setInputQuery('');
                    }}
                />

                <div className="size-8 flex justify-center items-center">
                    {isForceRefetch && <Spinner size={18} />}

                    {!isForceRefetch && (
                        <IconButton
                            variant="ghost"
                            icon={<Icon name="refresh" size={18} />}
                            tooltip={t('refresh-inbox')}
                            onClick={handleRefetch}
                        />
                    )}
                </div>
            </div>
            <div className="text-xs text-gray-500 mb-8 text-pretty">{t('inbox-description')}</div>

            {!data && isFetching && (
                <div className="text-center py-6">
                    <Loading />
                </div>
            )}

            {!query && data && emails.length === 0 && (
                <div className="text-xs text-center py-6">{t('inbox-empty')}</div>
            )}

            {query && data && emails.length === 0 && (
                <div className="text-xs text-center py-6">{t('inbox-no-results', { query })}</div>
            )}

            <div className="grid gap-2">
                {emails.map((mail) => (
                    <InvoicesInboxItem
                        id={mail.id}
                        sender={mail.sender}
                        subject={mail.subject}
                        date={mail.date}
                        files={mail.files}
                        onView={() => onViewEmail(mail)}
                        key={mail.id}
                    />
                ))}
            </div>

            <div className="mt-6 flex justify-center">
                {hasNextPage &&
                    (isFetching ? (
                        <Loading size={48} />
                    ) : (
                        <IconButton variant="ghost" icon={<OutlineArrowBottom />} onClick={fetchNextPage} />
                    ))}
            </div>

            {hasNextPage && <div ref={sentinelRef} />}
        </>
    );
}

InvoicesInboxList.propTypes = {
    onViewEmail: PropTypes.func.isRequired,
};
